import React, { useState } from 'react';
import { Typography, Box, Button, Grid, Modal } from '@mui/material';
import { dataURLtoFile, getText } from '../../libs/utils';
import { DownloadImage, PhotoComplete, OrderError, Warning, CloseIcon } from '../../assets/images/icons';
import cardPhoto from '../../assets/images/card-example.jpg';
import { styled } from '@mui/material/styles';
import REST from '../../libs/API';
import TimerTile from './TimerTile';
import ProgressBar from './ProgressBar';
import ProgressBarDots from './ProgressBarDots';
import Notice from '../main/Notice';

const InfoBox = styled(Box)(({ theme }) => ({
  "image": {
    margin: '0 0 25px',
  },
  " > .MuiBox-root": {
    marginTop: '25px'
  },
  [theme.breakpoints.only('md')]: {
    display: 'flex',
    gap: '34px',
    "img": {
      width: '31%',
    },
    " > .MuiBox-root": {
      width: '69%',
      marginTop: '10px'
    }
  },
  [theme.breakpoints.only('xs')]: {
    "img": {
      width: '100%',
    },
  }
}))

const DownloadBox = styled(Box)(({ theme }) => ({
  background: '#F8F8F8',
  borderRadius: '24px',
  height: '100%',
  justifyContent: 'center',
  display: 'flex',
  flexFlow: 'column',
  alignItems: 'center',
  minHeight: '300px',
  padding: '15px 0',
  [theme.breakpoints.only('md')]: {
    marginTop: '30px',
    height: 'auto',
  },
  [theme.breakpoints.only('xs')]: {
    marginTop: '18px',
    height: 'auto',
  }
}));

const ModalImage = styled(Box)(({ theme }) => ({
  "img": {
    width: '700px',
    maxWidth: '90%',
    position: 'absolute',
    top: '50%',
    left: ' 50%',
    transform: 'translate(-50%, -50%)',
  }
}))

const OrderVerify = ({ order, getStatus, handleTimeOut }) => {

  const [verifyLoading, setVerifyLoading] = useState(false);
  const [pictureLoaded, setPictureLoaded] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [imageModal, setImageModal] = useState(false);
  const [photos, setPhotos] = useState([]);


  const onChangeVerifier = e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      return;
    }
    if (photos.length === 1 && files.length === 1) {
      setPhotos((prevList => [...prevList, ...files]));
    } else {
      setPhotos(files.slice(0, 2));
    }
    e.target.value = null;
  }

  const uploadPhotos = async () => {
    if (verifyLoading) return;
    setVerifyLoading(true);
    setLoadingError(false);

    try {
      await REST.sendPhotoVerifyMulti(order.code, photos);
      setPictureLoaded(true);
      setPhotos([]);

      getStatus();

    } catch (error) {
      setLoadingError(true);
    } finally {
      setTimeout(() => {
        setVerifyLoading(false)
        setPictureLoaded(false);
      }, 3000);
    }
  };

  const loadPhotoBox = () => {
    if (verifyLoading && !loadingError) {
      return <>
        <ProgressBarDots done={pictureLoaded} />
        <Typography sx={{ display: 'block', color: 'secondary.main', fontSize: '12px' }}>{getText('uploadingPhoto')}</Typography>
      </>
    } else {
      if (order.nextState && order.nextState.verification.statusId === 10) { // Переход на оплату
        return <>
          <PhotoComplete />
          <Typography sx={{ display: 'block', color: 'primary.green', fontSize: '12px', marginTop: '20px' }}>{getText('verifyStteBlockSubhed10')}</Typography>
        </>
      } else if (order.verification.statusId === 1 && !loadingError) { // Ожидание проверки
        return <>
          <span className="dot-spin"></span>
          <Typography sx={{ display: 'block', fontSize: '12px' }}>{getText('verificationIsRunning')}</Typography>
        </>
      } else if (order.verification.statusId === 0 && !loadingError) {
        return <>
          {photos.length > 0 && <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '8px',
            justifyItems: 'center',
            marginBottom: '20%',
          }}>
            {photos.map((photo, index) => <Typography variant={"subline"} color="secondary" sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}>
              <span>{photo.name.length > 30 ? photo.name.slice(0, 10) + "..." + photo.name.slice(-10) : photo.name}</span>
              <CloseIcon sx={{ cursor: 'pointer' }} onClick={() => setPhotos(photos.filter((item, i) => i !== index))} />
            </Typography>)}
            <Button sx={{
              marginTop: '12px',
              maxWidth: '300px',
              padding: '14px 20px',
            }} variant={"contained"} color="primary" onClick={() => uploadPhotos()}>
              {getText("verifyNewSendBtn")}
            </Button>
          </Box>}
          <label htmlFor="photofile-input" style={{ cursor: 'pointer', textAlign: 'center' }}>
            <DownloadImage />
            <Typography sx={{ display: 'block', color: 'secondary.main', fontSize: '12px', marginTop: '15px' }}>{getText('verifyLoadButtonText')}</Typography>
          </label>
        </>
      } else { // Error repeat
        return <Box sx={{ maxWidth: '300px', margin: '0 auto', textAlign: 'center' }}>
          <OrderError />
          {loadingError // Error during loading, timeout is ended
            ? <>
              <Typography sx={{ color: 'primary.darkRed', fontWeight: 600, margin: '10px 0 7px' }}>{getText('exConnectError')}</Typography>
              <Typography sx={{ fontSize: '12px' }}>{getText('verifyServerError')}</Typography>
            </>
            : <>
              <Typography sx={{ color: 'primary.darkRed', fontWeight: 600, margin: '10px 0 7px' }}>{getText('verifyDeclinedH3')}</Typography>
              <Typography sx={{ fontSize: '12px' }}>{getText('verifyDeclinedHint')}</Typography>
              <Typography variant="greyCaption" sx={{ margin: '12px 0 5px', display: 'block' }}>{getText('verifyDeclinedDescriptCaption')}:</Typography>
              <Typography variant="boldCaption" component="div">{order.verification.description}</Typography>
            </>
          }
          {photos.length > 0 && <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '8px',
            justifyItems: 'center',
            marginBottom: '16px',
          }}>
            {photos.map((photo, index) => <Typography variant={"subline"} color="secondary" sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}>
              <span>{photo.name.length > 30 ? photo.name.slice(0, 10) + "..." + photo.name.slice(-10) : photo.name}</span>
              <CloseIcon sx={{ cursor: 'pointer' }} onClick={() => setPhotos(photos.filter((item, i) => i !== index))} />
            </Typography>)}
            <Button sx={{
              marginTop: '12px',
              maxWidth: '300px',
            }} variant={"contained"} color="primary" onClick={() => uploadPhotos()}>
              {getText("verifyNewSendBtn")}
            </Button>
          </Box>}
          <Button variant={photos.length > 0 ? '' : 'contained'}  sx={{ margin: '20px 0 0' }}>
            <label htmlFor="photofile-input" style={{ cursor: 'pointer', textAlign: 'center' }}>
              {getText('verifyNewLoadBtn')}
            </label>
          </Button>
        </Box>
      }
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h3">{getText('orderHeaderStateVerify')}</Typography>
        <TimerTile timerEnd={order.timerEnd} handleTimeOut={handleTimeOut} />
      </Box>
      <Typography variant="regularCaption">{getText('newPhotoText')}</Typography>
      <input
        style={{ display: 'none' }}
        onChange={onChangeVerifier}
        accept="image/*"
        id="photofile-input"
        multiple
        type="file"
      />
      <Grid container columnSpacing={3} sx={{ marginTop: '30px' }}>
        <Grid item lg={5} md={12} sm={5} xs={12}>
          <InfoBox>
            <img src={cardPhoto} alt="" onClick={() => setImageModal(true)} style={{ cursor: 'pointer' }} />
            <Box>
              <Grid container>
                <Grid item xs={6}>
                  <Box sx={{ fontWeight: 600 }}>{getText('orderPage')}</Box>
                  <Box sx={{ color: 'primary.grey', fontSize: 12 }}>{getText('verifySubHRequire01C')}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontWeight: 600 }}>{getText('orderNumber')}</Box>
                  <Box sx={{ color: 'primary.grey', fontSize: 12 }}>{getText('verifySubHRequire02C')}</Box>
                </Grid>
              </Grid>

              <Grid container sx={{ marginTop: '15px' }}>
                <Grid item xs={6}>
                  <Box sx={{ fontWeight: 600, fontSize: 14 }}>{getText('verifySubHRequire04V')}</Box>
                  <Box sx={{ color: 'primary.grey', fontSize: 12 }}>{getText('verifySubHRequire04C')}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontWeight: 600 }}>JPG, PNG, HEIC</Box>
                  <Box sx={{ color: 'primary.grey', fontSize: 12 }}>{getText('verifySubHRequire03C')}</Box>
                </Grid>
              </Grid>
            </Box>
          </InfoBox>
          <Notice
            icon={<Warning />}
            sx={{
              marginTop: { xs: '24px', sm: '32px', md: '32px' },

            }}
          >
            <p>{getText('verifyVirtCardWarn')}</p>
          </Notice>
        </Grid>
        <Grid item lg={7} md={12} sm={7} xs={12}>
          <DownloadBox>
            {loadPhotoBox()}
          </DownloadBox>
        </Grid>
      </Grid >

      <Modal
        open={imageModal}
        onClose={() => setImageModal(false)}
      //hideBackdrop={true}
      >
        <ModalImage><img src={cardPhoto} alt="" /></ModalImage>
      </Modal>
    </>
  )
}

export default OrderVerify